import styled, { css } from 'styled-components';

/* The form panel shared by every add/edit (and view) page. Its new-design look comes from the
   --panel-* tokens in base.scss, whose plain :root values are the legacy ones — so this restyles
   with the flag on and is untouched with it off. */
export const Section = styled.section`
  position: relative;
  border: 1px solid var(--grey);
  border-radius: var(--panel-radius);
  font-weight: normal;
  background: var(--white);
`;

export const Title = styled.h2`
  position: relative;
  z-index: 2;
  border-bottom: 1px solid var(--panel-head-border);
  height: 50px;
  background: var(--panel-head-bg);
  /* One pixel tighter than the panel's own radius: the header sits INSIDE the panel's 1px
     border, so it follows the border's inner curve. The panel rounds its matching corners --
     itself when standalone, via the grouped mixins below when several share one card. */
  border-radius: var(--panel-radius-inner) var(--panel-radius-inner) 0 0;
  font-size: 14px;
  font-weight: var(--panel-head-weight);
  line-height: 50px;
  padding: 0 20px;
  color: var(--panel-head-color);
`;

export const Content = styled.div`
  position: relative;
  padding: 20px;
  height: 100%;
  color: var(--panel-text);

  ${({ isSubmission }) =>
    isSubmission &&
    css`
      padding: 10px 20px;
    `}

  > div:last-of-type {
    margin-bottom: 0;
  }
`;

export const VerticalBorder = styled.div.attrs(() => ({}))`
  ${({ left }) =>
    css`
      border-right: 1px solid #e0e0e0;
      position: absolute;
      left: ${left};
      height: 100%;
      z-index: 1;
    `};
`;

/**
 * Corner handling for sections that share ONE rounded wrapper (the add/edit page layouts).
 *
 * Only the wrapper is a card, so every section inside it is square and just the outermost corners
 * round — otherwise an inner section's rounded corner leaves a notch at the seam, and its tinted
 * header pokes outside the wrapper's rounded border.
 *
 * The wrapper cannot simply clip with `overflow: hidden`: <BaseSelect /> renders its menu inline
 * (no menuPortalTarget), so clipping cuts off the dropdown of any select near the panel's bottom
 * edge — and these are form pages full of them.
 *
 * Two variants because the outer corners differ by direction: pick the one matching the wrapper's
 * own layout.
 */
const squareInnerSections = css`
  /* Every nesting level — some pages put sections inside a section's content. The headers are
     squared too: the filled strip only follows a corner where the wrapper actually has one. */
  section {
    border-radius: 0;
  }

  section > ${Title} {
    border-radius: 0;
  }
`;

/**
 * Columns side by side: the leftmost rounds on the left, the rightmost on the right.
 *
 * A "column" is either a <section> child or a div grouping several stacked sections (Retailers and
 * Venues wrap their left column that way), so each corner is matched both ways. Keying off
 * `section:first-of-type` alone silently rounded the wrong panel on those two pages: the only
 * direct section child was the right-hand column, so it took both corners and the left column's
 * header stayed square.
 */
export const groupedSectionCornersRow = css`
  ${squareInnerSections}

  /* The panel's own border rounds together with its header. squareInnerSections above zeroes every
     section, and re-rounding only the header left the panel's SQUARE border corner poking out
     around the curve -- the pale hook at the top corners. */
  > section:first-child,
  > div:first-child > section:first-of-type {
    border-top-left-radius: var(--panel-radius);
  }

  > section:first-child > ${Title}, > div:first-child > section:first-of-type > ${Title} {
    border-top-left-radius: var(--panel-radius-inner);
  }

  > section:first-child,
  > div:first-child > section:last-of-type {
    border-bottom-left-radius: var(--panel-radius);
  }

  > section:last-child,
  > div:last-child > section:first-of-type {
    border-top-right-radius: var(--panel-radius);
  }

  > section:last-child > ${Title}, > div:last-child > section:first-of-type > ${Title} {
    border-top-right-radius: var(--panel-radius-inner);
  }

  > section:last-child,
  > div:last-child > section:last-of-type {
    border-bottom-right-radius: var(--panel-radius);
  }
`;

/** Sections stacked: the top one rounds both top corners, the bottom one both bottom corners. */
export const groupedSectionCornersColumn = css`
  ${squareInnerSections}

  /* Panel and header round together -- see the note in the row variant. */
  > section:first-of-type {
    border-top-left-radius: var(--panel-radius);
    border-top-right-radius: var(--panel-radius);
  }

  > section:first-of-type > ${Title} {
    border-top-left-radius: var(--panel-radius-inner);
    border-top-right-radius: var(--panel-radius-inner);
  }

  > section:last-of-type {
    border-bottom-left-radius: var(--panel-radius);
    border-bottom-right-radius: var(--panel-radius);
  }
`;
